<template>
  <v-container class="fill-height">
    <v-row justify="center" align="center" class="fill-height">
      <v-col cols="12" lg="8">
        <!-- Loading -->
        <v-card v-if="loading && !question" class="text-center pa-8">
          <v-progress-circular indeterminate color="primary" size="64" />
          <div class="mt-4">Cargando pregunta...</div>
        </v-card>

        <!-- Resultado de la respuesta -->
        <div v-else-if="showResult && answerResult">
          <v-card class="mb-4" elevation="3">
            <v-card-text class="text-center pa-8">
              <v-icon
                :color="answerResult.correct ? 'success' : 'error'"
                size="80"
                class="mb-4"
              >
                {{ answerResult.correct ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              <div class="text-h5 font-weight-medium mb-2" :class="answerResult.correct ? 'text-success' : 'text-error'">
                {{ answerResult.correct ? '¡Respuesta correcta!' : 'Respuesta incorrecta' }}
              </div>
              <div v-if="!answerResult.correct" class="text-body-1 text-medium-emphasis">
                La respuesta correcta era la opción <strong>{{ answerResult.correct_option }}</strong>
              </div>
              <div v-if="answerResult.timeout" class="text-body-2 text-warning mt-1">
                Se agotó el tiempo
              </div>
            </v-card-text>
          </v-card>

          <!-- Justificación -->
          <v-card v-if="answerResult.justification || answerResult.justification_image_url" class="mb-4" elevation="2">
            <v-card-title>
              <v-icon class="mr-2">mdi-lightbulb</v-icon>
              Justificación
            </v-card-title>
            <v-card-text>
              <div v-if="answerResult.justification" class="text-body-1">
                <MathRenderer :content="answerResult.justification" />
              </div>
              <div v-if="answerResult.justification_image_url" class="mt-3">
                <AuthenticatedImage
                  :src="answerResult.justification_image_url"
                  max-height="400"
                  image-class="border rounded"
                />
              </div>
            </v-card-text>
          </v-card>

          <v-card class="text-center pa-4" elevation="1">
            <v-btn
              color="primary"
              variant="elevated"
              size="large"
              @click="exitQuestion"
              prepend-icon="mdi-check"
            >
              Continuar
            </v-btn>
          </v-card>
        </div>

        <!-- Pregunta cargada -->
        <div v-else-if="question && !showResult">
          <!-- Header con timer -->
          <v-card class="mb-4" elevation="3">
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <div>
                  <h2>Responder Pregunta</h2>
                  <div class="d-flex flex-wrap gap-2 mt-2">
                    <v-chip
                      v-if="question.school_subject?.name"
                      color="primary"
                      variant="outlined"
                      size="small"
                      prepend-icon="mdi-book-open-variant"
                    >
                      {{ question.school_subject.name }}
                    </v-chip>
                    <v-chip
                      :color="getDifficultyColor(question.difficulty)"
                      variant="tonal"
                      size="small"
                      prepend-icon="mdi-speedometer"
                    >
                      {{ getDifficultyLabel(question.difficulty) }}
                    </v-chip>
                  </div>
                </div>

                <!-- Timer -->
                <div class="text-center">
                  <v-avatar
                    size="80"
                    :color="
                      timeRemaining <= 30 ? 'error' : timeRemaining <= 60 ? 'warning' : 'primary'
                    "
                    class="timer-avatar"
                  >
                    <span class="text-h5 font-weight-bold text-white">
                      {{ formatTime(timeRemaining) }}
                    </span>
                  </v-avatar>
                  <div class="text-caption mt-1">Tiempo restante</div>
                </div>
              </div>

              <!-- Barra de progreso del tiempo -->
              <v-progress-linear
                :model-value="timeProgress"
                :color="timeRemaining <= 30 ? 'error' : timeRemaining <= 60 ? 'warning' : 'primary'"
                height="6"
                class="mt-3"
                rounded
              />
            </v-card-text>
          </v-card>

          <!-- Contenido de la pregunta -->
          <v-card class="mb-4" elevation="2">
            <v-card-text>
              <!-- Enunciado -->
              <div class="question-statement mb-4">
                <h3 class="mb-3">Pregunta:</h3>
                <div class="text-h6">
                  <MathRenderer :content="question.statement" />
                </div>
              </div>

              <!-- Imagen del enunciado -->
              <div v-if="question.statement_image_url" class="mb-4">
                <AuthenticatedImage
                  :src="question.statement_image_url"
                  max-height="400"
                  image-class="border rounded"
                />
              </div>
            </v-card-text>
          </v-card>

          <!-- Opciones de respuesta -->
          <v-card class="mb-4" elevation="2">
            <v-card-title>
              <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
              Seleccione su respuesta:
            </v-card-title>
            <v-card-text>
              <v-radio-group
                v-model="selectedOption"
                class="mt-0"
                :disabled="submitting || timeRemaining <= 0"
              >
                <v-row>
                  <v-col
                    v-for="(option, index) in question.options"
                    :key="option.id || index"
                    cols="12"
                    md="6"
                  >
                    <v-card
                      :class="[
                        'option-card pa-3 cursor-pointer',
                        {
                          'selected-option': selectedOption === option.id,
                          'border-primary': selectedOption === option.id,
                        },
                      ]"
                      variant="outlined"
                      @click.stop="selectOption(option.id)"
                      :disabled="submitting || timeRemaining <= 0"
                    >
                      <div class="d-flex align-center">
                        <v-radio
                          :value="option.id"
                          :disabled="submitting || timeRemaining <= 0"
                          @click.stop="selectOption(option.id)"
                          id="option-{{ option.id }}"
                        />
                        <v-avatar
                          size="40"
                          :color="selectedOption === option.id ? 'primary' : 'grey-lighten-2'"
                          class="mr-3 ml-2"
                        >
                          <span class="text-h6 font-weight-bold">
                            {{ option.label }}
                          </span>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <!-- Texto de la opción -->
                          <div v-if="option.text" class="option-text">
                            <MathRenderer :content="option.text" />
                          </div>
                          <!-- Imagen de la opción -->
                          <div v-if="option.image_url" class="option-image mt-2">
                            <AuthenticatedImage
                              :src="option.image_url"
                              max-height="120"
                              image-class="border rounded"
                            />
                          </div>
                          <div v-if="!option.text && !option.image_url" class="text-disabled">
                            Opción sin contenido
                          </div>
                        </div>
                        <v-icon v-if="selectedOption === option.id" color="primary" class="ml-2">
                          mdi-check-circle
                        </v-icon>
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-card-text>
          </v-card>

          <!-- Botones de acción -->
          <v-card class="text-center pa-4" elevation="1">
            <v-btn
              color="grey"
              variant="outlined"
              class="mr-4"
              @click="confirmExit"
              :disabled="submitting"
              prepend-icon="mdi-exit-to-app"
            >
              Salir
            </v-btn>

            <v-btn
              color="primary"
              variant="elevated"
              size="large"
              @click="submitAnswer"
              :loading="submitting"
              :disabled="!selectedOption || timeRemaining <= 0"
              prepend-icon="mdi-send"
            >
              Enviar Respuesta
            </v-btn>
          </v-card>
        </div>

        <!-- Error -->
        <v-alert v-else-if="error" type="error" variant="tonal" class="mb-4">
          <v-icon>mdi-alert-circle</v-icon>
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- Dialog de confirmación para salir -->
    <v-dialog v-model="exitDialog" max-width="400">
      <v-card>
        <v-card-title>
          <v-icon color="warning" class="mr-2">mdi-exit-to-app</v-icon>
          Confirmar salida
        </v-card-title>
        <v-card-text>
          ¿Está seguro que desea salir? Perderá su progreso en esta pregunta.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="exitDialog = false"> Continuar </v-btn>
          <v-btn color="warning" variant="elevated" @click="exitQuestion"> Salir </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de tiempo agotado -->
    <v-dialog v-model="timeUpDialog" max-width="400" persistent>
      <v-card>
        <v-card-title>
          <v-icon color="error" class="mr-2">mdi-clock-alert</v-icon>
          Tiempo agotado
        </v-card-title>
        <v-card-text> Se ha agotado el tiempo para responder esta pregunta. </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="elevated" @click="confirmTimeUp"> Aceptar </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false"> Cerrar </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AuthenticatedImage from '@/components/common/AuthenticatedImage.vue'
import MathRenderer from '@/components/common/MathRenderer.vue'

export default {
  name: 'AnswerQuestion',

  components: {
    AuthenticatedImage,
    MathRenderer,
  },

  props: {
    questionId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      selectedOption: null,
      timeRemaining: 0,
      timeSpent: 0,
      totalTime: 0,
      timer: null,
      startTime: null,

      submitting: false,
      showResult: false,
      exitDialog: false,
      timeUpDialog: false,

      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    }
  },

  computed: {
    ...mapGetters('questions', ['currentQuestion', 'loading', 'error', 'answerResult']),
    ...mapGetters('auth', ['isStudent']),

    question() {
      return this.currentQuestion
    },

    timeProgress() {
      if (this.totalTime === 0) return 0
      return ((this.totalTime - this.timeRemaining) / this.totalTime) * 100
    },
  },

  async created() {
    // Solo estudiantes pueden responder preguntas
    if (!this.isStudent) {
      this.$router.push('/questions')
      return
    }

    await this.loadQuestion()
  },

  beforeUnmount() {
    this.clearTimer()
  },

  methods: {
    ...mapActions('questions', ['fetchQuestionById', 'answerQuestion', 'clearAnswerData']),

    async loadQuestion() {
      const result = await this.fetchQuestionById(this.questionId)

      if (result.success) {
        this.initializeTimer()
      } else {
        this.showSnackbar(result.error, 'error')
      }
    },

    initializeTimer() {
      if (!this.question?.time_limit) return

      this.totalTime = this.question.time_limit
      this.timeRemaining = this.question.time_limit
      this.startTime = Date.now()

      this.timer = setInterval(() => {
        this.timeRemaining--
        this.timeSpent = Math.floor((Date.now() - this.startTime) / 1000)

        if (this.timeRemaining <= 0) {
          this.handleTimeUp()
        }
      }, 1000)
    },

    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    selectOption(optionId) {
      // Solo permitir selección si no está enviando y hay tiempo
      if (!this.submitting && this.timeRemaining > 0) {
        // Forzar el valor directamente para garantizar exclusividad
        // El v-radio-group automáticamente desmarca las demás opciones
        this.selectedOption = optionId
      }
    },

    async submitAnswer() {
      if (!this.selectedOption || this.timeRemaining <= 0) {
        this.showSnackbar('Por favor seleccione una respuesta', 'warning')
        return
      }

      this.submitting = true
      this.clearTimer()

      const finalTimeSpent = Math.floor((Date.now() - this.startTime) / 1000)

      const result = await this.answerQuestion({
        questionId: this.questionId,
        selectedOptionId: this.selectedOption,
        timeSpent: finalTimeSpent,
      })

      this.submitting = false

      if (result.success) {
        this.showResult = true
      } else {
        this.showSnackbar(result.error, 'error')
        this.initializeTimer() // Reiniciar timer si hay error
      }
    },

    handleTimeUp() {
      this.clearTimer()
      this.timeUpDialog = true
    },

    async confirmTimeUp() {
      this.timeUpDialog = false

      // Si no ha seleccionado opción, navegar sin enviar respuesta
      if (!this.selectedOption) {
        this.clearAnswerData()
        this.$router.push('/questions')
        return
      }

      // Enviar respuesta automáticamente
      await this.submitAnswer()
    },

    confirmExit() {
      this.exitDialog = true
    },

    exitQuestion() {
      this.clearTimer()
      this.clearAnswerData()
      this.showResult = false
      this.$router.push('/questions')
    },

    getDifficultyColor(difficulty) {
      const colors = {
        easy: 'success',
        medium: 'warning',
        hard: 'error',
      }
      return colors[difficulty] || 'grey'
    },

    getDifficultyLabel(difficulty) {
      const labels = {
        easy: 'Fácil',
        medium: 'Medio',
        hard: 'Difícil',
      }
      return labels[difficulty] || difficulty
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    showSnackbar(message, color = 'success', timeout = 4000) {
      this.snackbar = {
        show: true,
        message,
        color,
        timeout,
      }
    },
  },
}
</script>

<style scoped>
.question-statement {
  background: rgba(var(--v-theme-primary), 0.05);
  border-radius: 8px;
  padding: 16px;
}

.option-card {
  transition: all 0.2s ease;
  cursor: pointer;
}

.option-card:hover {
  elevation: 2;
  transform: translateY(-1px);
}

.option-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.selected-option {
  background: rgba(var(--v-theme-primary), 0.05);
}

.border-primary {
  border-color: rgb(var(--v-theme-primary)) !important;
  border-width: 2px !important;
}

.timer-avatar {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(var(--v-theme-primary), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0);
  }
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.rounded {
  border-radius: 4px;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
